import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Paginator from '../../components/ui/Paginator';
import Message from '../../components/ui/Message';
import { costingApi, formatQuantity } from './costing';

const PAGE_SIZE = 15;

type Line = { productId: number | ''; quantity: string };

// Traslados entre sucursales de la misma cadena: sale al costo promedio de esta sucursal y entra a la otra
// con ese costo. El producto destino es el que tiene el mismo código.
export const TransfersPage = () => {
    const navigate = useNavigate();
    const [destinations, setDestinations] = useState<{ id: number; name: string }[]>([]);
    const [products, setProducts] = useState<any[]>([]);
    const [destinationId, setDestinationId] = useState('');
    const [note, setNote] = useState('');
    const [lines, setLines] = useState<Line[]>([{ productId: '', quantity: '' }]);
    const [saving, setSaving] = useState(false);

    const [page, setPage] = useState(1);
    const [data, setData] = useState<any>({ transfers: [], totalPages: 0 });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = async (p: number) => {
        const resp = await costingApi.transfers(p, PAGE_SIZE);
        if (resp.data) setData(resp.data);
    };

    useEffect(() => {
        costingApi.transferDestinations().then((r) => setDestinations(r.data?.destinations ?? []));
        costingApi.products().then((r) => setProducts((r.data?.stocks ?? []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name)))));
    }, []);
    useEffect(() => { load(page); }, [page]);

    const setLine = (i: number, patch: Partial<Line>) => setLines((prev) => prev.map((l, j) => (j === i ? { ...l, ...patch } : l)));

    const save = async () => {
        if (!destinationId) return setError('Elija la sucursal destino.');
        if (lines.some((l) => !l.productId || !(Number(l.quantity) > 0))) return setError('Elija el producto y una cantidad mayor que cero en cada línea.');
        setSaving(true);
        const resp = await costingApi.createTransfer({
            destinationId: Number(destinationId), note,
            lines: lines.map((l) => ({ productId: Number(l.productId), quantity: Number(l.quantity) })),
        });
        setSaving(false);
        if (resp.error) return setError(resp.error);
        setSuccess(`Se registró el traslado ${resp.data?.transferId}.`);
        setLines([{ productId: '', quantity: '' }]);
        setNote('');
        load(1);
        setPage(1);
    };

    return (
        <div className='mt-5 container-fluid px-4' style={{ flex: 1, marginTop: 10 }}>
            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Traslados entre sucursales</h3>
                    <p className="text-muted mb-0">Envíe productos a otra sucursal de su cadena, con su costo promedio.</p>
                </div>
                <button type="button" className="btn btn-outline-secondary mt-2 mt-md-0" onClick={() => navigate('/stocks')}>
                    <i className="bi bi-arrow-left me-1" /> Volver al inventario
                </button>
            </div>

            {destinations.length === 0 ? (
                <div className="alert alert-info">Este comercio no tiene otras sucursales en su cadena a las que trasladar.</div>
            ) : (
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                        <div className="row g-3 mb-3">
                            <div className="col-md-5">
                                <label htmlFor="destinationSelect" className="form-label">Sucursal destino *</label>
                                <select id="destinationSelect" className="form-select" value={destinationId} onChange={(e) => setDestinationId(e.target.value)}>
                                    <option value="">Elija una sucursal</option>
                                    {destinations.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                                </select>
                            </div>
                            <div className="col-md-7">
                                <label htmlFor="transferNoteInput" className="form-label">Nota</label>
                                <input id="transferNoteInput" className="form-control" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
                            </div>
                        </div>
                        {lines.map((line, i) => (
                            <div className="row g-2 mb-2" key={i}>
                                <div className="col-md-7">
                                    <select aria-label={`Producto ${i + 1}`} className="form-select" value={line.productId}
                                        onChange={(e) => setLine(i, { productId: e.target.value ? Number(e.target.value) : '' })}>
                                        <option value="">Elija un producto</option>
                                        {products.map((p) => <option key={p.id} value={p.id}>{p.code} · {p.name} (hay {formatQuantity(p.stock)})</option>)}
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <input aria-label={`Cantidad ${i + 1}`} type="number" min="0" step="any" className="form-control" placeholder="Cantidad"
                                        value={line.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} />
                                </div>
                                <div className="col-md-2">
                                    <button type="button" className="btn btn-outline-danger" title="Quitar" disabled={lines.length === 1}
                                        onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}><i className="bi bi-trash" /></button>
                                </div>
                            </div>
                        ))}
                        <div className="d-flex justify-content-between mt-3">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines((prev) => [...prev, { productId: '', quantity: '' }])}>
                                <i className="bi bi-plus-lg me-1" /> Agregar producto
                            </button>
                            <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                                {saving ? <><span className="spinner-border spinner-border-sm me-1" /> Guardando...</> : 'Registrar traslado'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="card border-0 shadow-sm">
                <div className="card-body">
                    <h6 className="mb-3">Traslados registrados</h6>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>N.º</th><th>Fecha</th><th>Desde</th><th>Hacia</th><th className="text-end">Productos</th><th>Nota</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.transfers.length === 0 ? (
                                    <tr><td colSpan={6} className="text-center py-4 text-muted">No hay traslados.</td></tr>
                                ) : data.transfers.map((t: any) => (
                                    <tr key={t.id}>
                                        <td>{t.id}</td>
                                        <td className="small text-nowrap">{t.date ? new Date(t.date).toLocaleString() : '-'}</td>
                                        <td>{t.originName}</td>
                                        <td>{t.destinationName}</td>
                                        <td className="text-end">{t.lines}</td>
                                        <td className="small text-muted">{t.note ?? '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {data.totalPages > 1 && <Paginator totalPage={data.totalPages} pageActual={page} onChange={(p) => setPage(p)} />}
                </div>
            </div>

            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Exitoso" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
